import { api } from ".";
import {
  SpatialDataset,
  SpatialDatasetCreatePayload,
  SpatialDatasetUpdatePayload,
  SpatialDatasetProcessingStatus,
  PaginatedResponse
} from "../types";

// ─── Response envelope helpers ───────────────────────────────────────────────
// POST /spatial-datasets/ and PATCH /spatial-datasets/{id}/ wrap the body in
// { message, data: {...} }. GET /spatial-datasets/{id}/ returns bare objects.
// See SPATIAL_DATASET_FRONTEND.md §2 for the full envelope table.

interface WrappedResponse<T> {
  message: string;
  data: T;
}

// ─── List query params ───────────────────────────────────────────────────────

export interface SpatialDatasetListParams {
  dataset_type?: "raster" | "vector" | "tabular";
  processing_status?: SpatialDatasetProcessingStatus;
  search?: string;
  bbox?: string; // "minLng,minLat,maxLng,maxLat"
  time_series_group_id?: string;
  ordering?: string;
  include_relations?: boolean;
  page?: number;
  page_size?: number;
}

// ─── Multipart types ─────────────────────────────────────────────────────────

export interface MultipartInitializePayload {
  filename: string;
  parts: number;
  fileSize: number;
  fileType: string;
}

export interface MultipartInitializeResponse {
  upload_id: string;
  urls: string[];
  calculation: Record<string, any>;
}

export interface MultipartPart {
  PartNumber: number;
  ETag: string;
}

export interface MultipartCompleteResponse {
  detail: string;
  location: string;
  etag: string;
  processing_triggered: boolean;
}

export interface ProcessResponse {
  processing_triggered: boolean;
  processing_status: SpatialDatasetProcessingStatus;
}

export interface DownloadResponse {
  url: string;
  filename: string;
  expires_in: number;
}

// ─── CRUD ────────────────────────────────────────────────────────────────────

// POST returns { message, data: {...} } — read res.data.data (see §2)
export const createSpatialDataset = async (payload: SpatialDatasetCreatePayload): Promise<SpatialDataset> => {
  const res = await api.post<WrappedResponse<SpatialDataset>>("api/v2/spatial-datasets/", payload);
  return res.data.data;
};

export const listSpatialDatasets = async (params?: SpatialDatasetListParams): Promise<PaginatedResponse<SpatialDataset>> => {
  const res = await api.get<PaginatedResponse<SpatialDataset>>("api/v2/spatial-datasets/", { params });
  return res.data;
};

// GET /{id}/ returns a bare object — no `data` wrapper (see §2)
export const getSpatialDataset = async (id: number, params?: { include_relations?: boolean }): Promise<SpatialDataset> => {
  const res = await api.get<SpatialDataset>(`api/v2/spatial-datasets/${id}/`, { params });
  return res.data;
};

// PATCH /{id}/ wraps the response in { message, data: {...} }
export const updateSpatialDataset = async (id: number, payload: SpatialDatasetUpdatePayload): Promise<SpatialDataset> => {
  const res = await api.patch<WrappedResponse<SpatialDataset>>(`api/v2/spatial-datasets/${id}/`, payload);
  return res.data.data;
};

export const deleteSpatialDataset = async (id: number): Promise<void> => {
  await api.delete(`api/v2/spatial-datasets/${id}/`);
};

// ─── Upload flow ─────────────────────────────────────────────────────────────
// The frontend always uses multipart upload (SPATIAL_DATASET_FRONTEND (2).md §0 §7).
// sign/ exists on the backend but is NOT used by the frontend.
// processSpatialDataset() is kept for re-running failed conversions from SpatialDatasetDetail.

// rasterOutput only applies to geotiff — ignored for vectors
export const processSpatialDataset = async (id: number, rasterOutput?: "cog" | "pmtiles"): Promise<ProcessResponse> => {
  const payload = rasterOutput ? { raster_output: rasterOutput } : {};
  const res = await api.post<ProcessResponse>(`api/v2/spatial-datasets/${id}/process/`, payload);
  return res.data;
};

// ─── Multipart upload ────────────────────────────────────────────────────────
// Chunk size must be exactly 10 MB (backend rejects mismatched part count).
// complete/ auto-triggers conversion using raster_output set at create time — no process/ call.
// urls array has `parts + 2` entries — use only the first `parts` (spares for retries).

export const initializeMultipartUpload = async (id: number, payload: MultipartInitializePayload): Promise<MultipartInitializeResponse> => {
  const res = await api.post<MultipartInitializeResponse>(`api/v2/spatial-datasets/${id}/multipart/initialize/`, payload);
  return res.data;
};

export const completeMultipartUpload = async (uploadId: string, parts: MultipartPart[]): Promise<MultipartCompleteResponse> => {
  const res = await api.post<MultipartCompleteResponse>("api/v2/spatial-datasets/multipart/complete/", { upload_id: uploadId, parts });
  return res.data;
};

export const abortMultipartUpload = async (uploadId: string): Promise<void> => {
  await api.post("api/v2/spatial-datasets/multipart/abort/", {
    upload_id: uploadId
  });
};

// ─── Download ─────────────────────────────────────────────────────────────────

export const getSpatialDatasetDownloadUrl = async (id: number): Promise<DownloadResponse> => {
  const res = await api.get<DownloadResponse>(`api/v2/spatial-datasets/${id}/download/`);
  return res.data;
};

// ─── Tabular CSV rows (read-only in-app viewer) ──────────────────────────────
// GET /api/v2/csv-data/?file_key=<original_file_key>&limit=&offset= returns the
// CSV parsed to JSON rows. Uses the normal login Bearer (not a data-access token).
// The endpoint path is config-driven (config.tabularData.csvData) so it is passed
// in. The response envelope is not formally documented yet — callers normalise it
// defensively (see useCsvData).

export interface CsvDataParams {
  file_key: string;
  limit: number;
  offset: number;
}

export const getCsvData = async (endpoint: string, params: CsvDataParams): Promise<unknown> => {
  // The axios `api` instance already has REACT_APP_API_ROOT as baseURL, so strip a
  // leading slash from the (possibly config-provided) endpoint path.
  const path = endpoint.replace(/^\/+/, "");
  const res = await api.get(path, { params });
  return res.data;
};
